import React, {useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useLot} from "../../../utilities/contexts/lot-context";
import {doGetLotBalance, doGetLotHeader, doGetLotRows, doGetLotSales} from "../../../logics/lots";
import Loading from "../../UI/Loading";
import {formatInt, formatPrice, priceFormat, parseDate, dateToFileName} from "../../../utilities/data/parser";
import {useReactToPrint} from "react-to-print";
import {printPageStyle} from "../../../utilities/data/tools";
import {CSVLink} from "react-csv";
import CsvButton from "../../UI/CSVButton";
import PrintButton from "../../UI/PrintButton";

const LotRecap = () => {
    const {nr} = useParams();
    const {lotHeader, lotRows, balance, loadLotHeader, loadRows, loadBalance} = useLot();
    const [loading, setLoading] = useState(true);
    const [soldSales, setSoldSales] = useState([]);
    const [unsoldSales, setUnsoldSales] = useState([]);
    const printRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        const loadAll = async () => {
            try {
                setLoading(true);
                const [h, r, b, s] = await Promise.all([
                    doGetLotHeader(nr),
                    doGetLotRows(nr),
                    doGetLotBalance(nr),
                    doGetLotSales(nr)
                ]);
                if (!mounted) return;
                const headerData = h?.data?.data || h;
                if (headerData) loadLotHeader(headerData);
                if (Array.isArray(r)) loadRows(r);
                const balanceData = b?.data?.data || b;
                if (balanceData) loadBalance(balanceData);
                const salesData = s?.data?.data || s;
                const sold = salesData?.sold_leathers ?? salesData?.sold ?? [];
                const unsold = salesData?.unsold_leathers ?? salesData?.unsold ?? [];
                setSoldSales(Array.isArray(sold) ? sold : []);
                setUnsoldSales(Array.isArray(unsold) ? unsold : []);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Errore caricamento riepilogo lotto', e);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        loadAll();
        return () => { mounted = false; };
    }, [nr, loadLotHeader, loadRows, loadBalance]);

    const handlePrint = useReactToPrint({
        pageStyle: printPageStyle,
        content: () => printRef.current,
        documentTitle: `riepilogo_lotto_${nr}_${dateToFileName(new Date().toISOString().split('T')[0])}`
    });

    const getCsvFileName = () => `riepilogo_lotto_${nr}_${dateToFileName(new Date().toISOString().split('T')[0])}`;

    const buildCsvData = () => {
        const rows = [];
        const h = lotHeader || {};
        const docType = h?.document?.type?.nome || h?.document?.type?.name || "";
        const docNr = h?.nrdocfor || "";
        const docDate = h?.datadocfor ? parseDate(h.datadocfor) : "";
        const docFull = [docType, docNr, docDate].filter(Boolean).join(" ");

        // Intestazione
        rows.push([`Intestazione lotto ${nr}`]);
        rows.push(["Nr Lotto", "Fornitore", "Documento in ingresso", "Valuta", "UM Superficie"]);
        rows.push([
            h?.nrlotto || nr,
            h?.supplier?.name || "",
            docFull,
            h?.valuta || "",
            h?.um?.name || "",
        ]);
        rows.push([""]); // separatore

        // Righe
        rows.push(["Righe"]);
        rows.push(["Articolo", "Colore", "Scelta", "Superficie", "Nr.Pz", "Prezzo Un."]);
        (lotRows || []).forEach(r => {
            rows.push([
                r?.article?.name || "",
                r?.color?.name || "",
                r?.choice?.name || "",
                formatPrice(r?.qta),
                formatInt(r?.nrpz),
                formatPrice(r?.prezzo),
            ]);
        });

        // Riepilogo (se disponibile)
        if (balance) {
            rows.push([""]);
            rows.push(["Riepilogo Pelli"]);
            rows.push(["Pelli", "Nr", "Resa", "mq"]);
            rows.push(["acquistate", formatInt(balance?.leathers?.purchased?.amount), formatPrice(balance?.leathers?.purchased?.performance), formatPrice(balance?.leathers?.purchased?.mt)]);
            rows.push(["vendute", formatInt(balance?.leathers?.sold?.amount), formatPrice(balance?.leathers?.sold?.performance), formatPrice(balance?.leathers?.sold?.mt)]);
            rows.push(["in magazzino", formatInt(balance?.leathers?.in_house), "", ""]);
            rows.push(["resi", formatInt(balance?.leathers?.returned), "", ""]);
            rows.push(["Differenza", formatInt(balance?.leathers?.difference?.amount), `${formatInt(balance?.leathers?.difference?.performance)}%`, ""]);

            rows.push([""]); // separatore
            // Riepilogo Economico
            rows.push(["Riepilogo Economico"]);
            rows.push(["", "Costi", "Ricavi", "Profitto"]);
            rows.push(["AL PIEDE", `${priceFormat(balance?.balance?.per_foot?.cost)}€`, `${priceFormat(balance?.balance?.per_foot?.revenue)}€`, `${priceFormat(balance?.balance?.per_foot?.margin)}€`]);
            rows.push(["ALLA PELLE", `${priceFormat(balance?.balance?.per_leather?.cost)}€`, `${priceFormat(balance?.balance?.per_leather?.revenue)}€`, `${priceFormat(balance?.balance?.per_leather?.margin)}€`]);
            rows.push(["AL METRO", `${priceFormat(balance?.balance?.per_mt?.cost)}€`, `${priceFormat(balance?.balance?.per_mt?.revenue)}€`, `${priceFormat(balance?.balance?.per_mt?.margin)}€`]);
            rows.push(["TOTALE", `${priceFormat(balance?.balance?.totals?.cost)}€`, `${priceFormat(balance?.balance?.totals?.revenue)}€`, `${priceFormat(balance?.balance?.totals?.margin)}€`]);
        }

        // Vendite del lotto (dati nuovi)
        rows.push([""]);
        rows.push(["Vendite del lotto"]);
        rows.push(["Nr Lotto", "Provenienza", "Articolo - Colore", "Cliente", "Resa", "Costo Mq", "Ve/Mq", "Diff. %", "N. Mq Vend.", "Tot. costo", "Tot. Vend.", "Margine", "Tot. Nr. Pz"]);
        const toNum = (v) => (typeof v === 'string' && v.trim() === '') ? 0 : (Number(v) || 0);
        (soldSales || []).forEach(row => {
            const soldMq = toNum(row?.sold_mq);
            const costPerMq = toNum(row?.costs_per_mq);
            const sellPerMq = toNum(row?.sold_per_mq);
            const total_cost = row?.total_cost != null ? toNum(row.total_cost) : (costPerMq * soldMq);
            const total_profit = row?.total_profit != null ? toNum(row.total_profit) : (sellPerMq * soldMq);
            const margin = total_cost - total_profit;
            rows.push([
                row?.nrlotto || "",
                row?.buying_article || "",
                row?.sales_article || "",
                row?.client || "",
                formatPrice(row?.performance),
                formatPrice(costPerMq),
                formatPrice(sellPerMq),
                formatPrice(row?.diff_percentage),
                formatPrice(soldMq),
                formatPrice(total_cost),
                formatPrice(total_profit),
                formatPrice(margin),
                formatInt(row?.nrpz)
            ]);
        });

        // Resi (dati nuovi)
        if (unsoldSales && unsoldSales.length > 0) {
            rows.push([""]);
            rows.push(["Resi"]);
            rows.push(["Nr Lotto", "Provenienza", "Articolo - Colore", "Cliente", "Resa", "Costo Mq", "Ve/Mq", "Diff. %", "N. Mq Vend.", "Tot. costo", "Tot. Vend.", "Margine", "Tot. Nr. Pz"]);
            (unsoldSales || []).forEach(row => {
                const soldMq = toNum(row?.sold_mq);
                const costPerMq = toNum(row?.costs_per_mq);
                const sellPerMq = toNum(row?.sold_per_mq);
                const total_cost = row?.total_cost != null ? toNum(row.total_cost) : (costPerMq * soldMq);
                const total_profit = row?.total_profit != null ? toNum(row.total_profit) : (sellPerMq * soldMq);
                const margin = total_cost - total_profit;
                rows.push([
                    row?.nrlotto || "",
                    row?.buying_article || "",
                    row?.sales_article || "",
                    row?.client || "",
                    formatPrice(row?.performance),
                    formatPrice(costPerMq),
                    formatPrice(sellPerMq),
                    formatPrice(row?.diff_percentage),
                    formatPrice(soldMq),
                    formatPrice(total_cost),
                    formatPrice(total_profit),
                    formatPrice(margin),
                    formatInt(row?.nrpz)
                ]);
            });
        }

        return rows;
    };

    if (loading) return <Loading label="Caricamento riepilogo lotto" />;

    return (
        <div className="flex flex-col flex-1 overflow-y-auto gap-4 pb-24 px-4 md:px-8 lg:px-12">
            <div ref={printRef}>
                {/* Print/Header title */}
                <div className="mb-3 text-center print:mb-2">
                    <div className="text-2xl font-semibold tracking-wide print:text-xl">Riepilogo lotto {nr}</div>
                    <div className="text-sm text-gray-600 print:text-black">
                        {lotHeader?.supplier?.name ? `${lotHeader.supplier.name} — ` : ''}
                        {lotHeader?.datadocfor ? `doc. ${lotHeader?.nrdocfor || ''} del ${parseDate(lotHeader.datadocfor)}` : ''}
                    </div>
                </div>
                {/* Intestazione lotto - card style */}
                <div className="rounded-md border border-black p-4 shadow-sm print:shadow-none print:border-black" style={{pageBreakInside: 'avoid'}}>
                    <div className="text-lg font-semibold tracking-wide mb-3">Intestazione lotto {nr}</div>
                    <div className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm print:text-base">
                        <div className="font-medium text-gray-700 print:text-black">Nr Lotto</div>
                        <div className="text-right whitespace-nowrap">{lotHeader?.nrlotto || nr}</div>
                        <div className="border-l border-gray-300 pl-3"></div>
                        <div></div>

                        <div className="font-medium text-gray-700 print:text-black">Fornitore</div>
                        <div className="text-right truncate" title={lotHeader?.supplier?.name || ''}>{lotHeader?.supplier?.name || ''}</div>
                        <div className="border-l border-gray-300 pl-3"></div>
                        <div></div>

                        <div className="font-medium text-gray-700 print:text-black">Documento in ingresso</div>
                        <div className="text-right truncate" title={`${(lotHeader?.document?.type?.nome || lotHeader?.document?.type?.name || '')} ${lotHeader?.nrdocfor || ''} ${lotHeader?.datadocfor ? parseDate(lotHeader?.datadocfor) : ''}`}>
                            {(lotHeader?.document?.type?.nome || lotHeader?.document?.type?.name || '')} {lotHeader?.nrdocfor} {lotHeader?.datadocfor ? parseDate(lotHeader?.datadocfor) : ''}
                        </div>
                        <div className="border-l border-gray-300 pl-3"></div>
                        <div></div>

                        <div className="font-medium text-gray-700 print:text-black">Valuta</div>
                        <div className="text-right whitespace-nowrap">{lotHeader?.valuta || ''}</div>
                        <div className="border-l border-gray-300 pl-3"></div>
                        <div></div>

                        <div className="font-medium text-gray-700 print:text-black">UM Superficie</div>
                        <div className="text-right whitespace-nowrap">{lotHeader?.um?.name || ''}</div>
                        <div className="border-l border-gray-300 pl-3"></div>
                        <div></div>
                    </div>
                </div>

                {/* Righe - card style */}
                <div className="rounded-md border border-black p-4 mt-4 shadow-sm print:shadow-none print:border-black" style={{pageBreakInside: 'avoid'}}>
                    <div className="text-lg font-semibold tracking-wide mb-2">Righe</div>
                    <div className="mt-1">
                        <table className="w-full text-sm print:text-base">
                            <thead>
                            <tr className="border-b border-black">
                                <th className="text-left py-1">Articolo</th>
                                <th className="text-left py-1">Colore</th>
                                <th className="text-left py-1">Scelta</th>
                                <th className="text-right py-1">Superficie</th>
                                <th className="text-right py-1">Nr.Pz</th>
                                <th className="text-right py-1">Prezzo Un.</th>
                            </tr>
                            </thead>
                            <tbody>
                            {(lotRows || []).map((row) => (
                                <tr key={row?.idriga || `${row?.article?.name}-${row?.color?.name}-${row?.choice?.name}`}> 
                                    <td className="text-left py-1">{row?.article?.name}</td>
                                    <td className="text-left py-1">{row?.color?.name}</td>
                                    <td className="text-left py-1">{row?.choice?.name}</td>
                                    <td className="text-right py-1">{formatPrice(row?.qta)}</td>
                                    <td className="text-right py-1">{formatInt(row?.nrpz)}</td>
                                    <td className="text-right py-1">{formatPrice(row?.prezzo)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Riepilogo - resa più pulita e stampabile */}
                {balance && (
                    <div className="mt-6 space-y-6">
                        {/* Riepilogo Pelli */}
                        <div className="rounded-md border border-black p-4 shadow-sm print:shadow-none print:border-black" style={{pageBreakInside: 'avoid'}}>
                            <div className="text-lg font-semibold tracking-wide mb-3">Riepilogo Pelli</div>
                            <div className="grid grid-cols-4 gap-y-2 gap-x-6 text-sm print:text-base">
                                {/* Header invisibile per mantenere spaziature (opzionale) */}
                                {/* <div></div><div></div><div></div><div></div> */}

                                <div className="font-medium text-gray-700 print:text-black">Acquistate</div>
                                <div className="text-right whitespace-nowrap min-w-[5rem]">{formatInt(balance?.leathers?.purchased?.amount)} pz</div>
                                <div className="text-right whitespace-nowrap min-w-[4rem] border-l border-gray-300 pl-3">{formatPrice(balance?.leathers?.purchased?.performance)}%</div>
                                <div className="text-right whitespace-nowrap min-w-[4rem]">{formatPrice(balance?.leathers?.purchased?.mt)} mq</div>

                                <div className="font-medium text-gray-700 print:text-black">Vendute</div>
                                <div className="text-right whitespace-nowrap min-w-[5rem]">{formatInt(balance?.leathers?.sold?.amount)} pz</div>
                                <div className="text-right whitespace-nowrap min-w-[4rem] border-l border-gray-300 pl-3">{formatPrice(balance?.leathers?.sold?.performance)}%</div>
                                <div className="text-right whitespace-nowrap min-w-[4rem]">{formatPrice(balance?.leathers?.sold?.mt)} mq</div>

                                <div className="font-medium text-gray-700 print:text-black">In magazzino</div>
                                <div className="text-right whitespace-nowrap min-w-[5rem]">{formatInt(balance?.leathers?.in_house)} pz</div>
                                <div className="border-l border-gray-300 pl-3"></div>
                                <div></div>

                                <div className="font-medium text-gray-700 print:text-black">Resi</div>
                                <div className="text-right whitespace-nowrap min-w-[5rem]">{formatInt(balance?.leathers?.returned)} pz</div>
                                <div className="border-l border-gray-300 pl-3"></div>
                                <div></div>

                                <div className="font-semibold text-gray-900 print:text-black">Differenza</div>
                                <div className="text-right font-semibold whitespace-nowrap min-w-[5rem]">{formatInt(balance?.leathers?.difference?.amount)} pz</div>
                                <div className="text-right font-semibold whitespace-nowrap min-w-[4rem] border-l border-gray-300 pl-3">{formatInt(balance?.leathers?.difference?.performance)}%</div>
                                <div></div>
                            </div>
                        </div>

                        {/* Vendite del lotto */}
                        <div className="rounded-md border border-black p-4 shadow-sm print:shadow-none print:border-black" style={{pageBreakInside: 'avoid'}}>
                            <div className="text-lg font-semibold tracking-wide mb-2">Vendite del lotto</div>
                            <div className="mt-1">
                                <table className="w-full text-sm print:text-base">
                                    <thead>
                                    <tr className="border-b border-black">
                                        <th className="text-left py-1">Nr Lotto</th>
                                        <th className="text-left py-1">Provenienza</th>
                                        <th className="text-left py-1">Articolo - Colore</th>
                                        <th className="text-left py-1">Cliente</th>
                                        <th className="text-right py-1 pr-1">Resa</th>
                                        <th className="text-right py-1 pr-1">Costo Mq</th>
                                        <th className="text-right py-1 pr-1">Ve/Mq</th>
                                        <th className="text-right py-1 pr-1">Diff. %</th>
                                        <th className="text-right py-1 pr-1">N. Mq Vend.</th>
                                        <th className="text-right py-1 pr-1">Tot. costo</th>
                                        <th className="text-right py-1 pr-1">Tot. Vend.</th>
                                        <th className="text-right py-1 pr-1">Margine</th>
                                        <th className="text-right py-1 pr-1">Tot. Nr. Pz</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {soldSales && soldSales.length > 0 ? (
                                        soldSales.map((row, i) => {
                                            const toNum = (v) => (typeof v === 'string' && v.trim() === '') ? 0 : (Number(v) || 0);
                                            const soldMq = toNum(row?.sold_mq);
                                            const costPerMq = toNum(row?.costs_per_mq);
                                            const sellPerMq = toNum(row?.sold_per_mq);
                                            const total_cost = row?.total_cost != null ? toNum(row.total_cost) : (costPerMq * soldMq);
                                            const total_profit = row?.total_profit != null ? toNum(row.total_profit) : (sellPerMq * soldMq); // in report questo campo rappresenta il totale vendite
                                            const margin = total_cost - total_profit; // mantenuto coerente con SalesRow
                                            return (
                                                <tr key={i}>
                                                    <td className="text-left py-1">{row?.nrlotto}</td>
                                                    <td className="text-left py-1">{row?.buying_article}</td>
                                                    <td className="text-left py-1">{row?.sales_article}</td>
                                                    <td className="text-left py-1">{row?.client}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(row?.performance)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(costPerMq)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(sellPerMq)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(row?.diff_percentage)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(soldMq)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(total_cost)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(total_profit)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(margin)}</td>
                                                    <td className="text-right py-1 pr-1">{formatInt(row?.nrpz)}</td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={13} className="text-center py-2 text-gray-600 print:text-black">Nessuna vendita trovata per questo lotto</td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {unsoldSales && unsoldSales.length > 0 && (
                            <div className="rounded-md border border-black p-4 shadow-sm print:shadow-none print:border-black" style={{pageBreakInside: 'avoid'}}>
                                <div className="text-lg font-semibold tracking-wide mb-2">Resi</div>
                                <div className="mt-1">
                                    <table className="w-full text-sm print:text-base">
                                        <thead>
                                        <tr className="border-b border-black">
                                            <th className="text-left py-1">Nr Lotto</th>
                                            <th className="text-left py-1">Provenienza</th>
                                            <th className="text-left py-1">Articolo - Colore</th>
                                            <th className="text-left py-1">Cliente</th>
                                            <th className="text-right py-1 pr-1">Resa</th>
                                            <th className="text-right py-1 pr-1">Costo Mq</th>
                                            <th className="text-right py-1 pr-1">Ve/Mq</th>
                                            <th className="text-right py-1 pr-1">Diff. %</th>
                                            <th className="text-right py-1 pr-1">N. Mq Vend.</th>
                                            <th className="text-right py-1 pr-1">Tot. costo</th>
                                            <th className="text-right py-1 pr-1">Tot. Vend.</th>
                                            <th className="text-right py-1 pr-1">Margine</th>
                                            <th className="text-right py-1 pr-1">Tot. Nr. Pz</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {unsoldSales.map((row, i) => {
                                            const toNum = (v) => (typeof v === 'string' && v.trim() === '') ? 0 : (Number(v) || 0);
                                            const soldMq = toNum(row?.sold_mq);
                                            const costPerMq = toNum(row?.costs_per_mq);
                                            const sellPerMq = toNum(row?.sold_per_mq);
                                            const total_cost = row?.total_cost != null ? toNum(row.total_cost) : (costPerMq * soldMq);
                                            const total_profit = row?.total_profit != null ? toNum(row.total_profit) : (sellPerMq * soldMq);
                                            const margin = total_cost - total_profit;
                                            return (
                                                <tr key={i}>
                                                    <td className="text-left py-1">{row?.nrlotto}</td>
                                                    <td className="text-left py-1">{row?.buying_article}</td>
                                                    <td className="text-left py-1">{row?.sales_article}</td>
                                                    <td className="text-left py-1">{row?.client}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(row?.performance)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(costPerMq)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(sellPerMq)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(row?.diff_percentage)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(soldMq)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(total_cost)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(total_profit)}</td>
                                                    <td className="text-right py-1 pr-1">{formatPrice(margin)}</td>
                                                    <td className="text-right py-1 pr-1">{formatInt(row?.nrpz)}</td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="rounded-md border border-black p-4 shadow-sm print:shadow-none print:border-black" style={{pageBreakInside: 'avoid'}}>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                    <div>
                        <div className="text-lg font-semibold tracking-wide">Riepilogo Economico</div>
                        <div className="text-xs text-gray-600 print:text-black">Costi, ricavi e margini</div>
                    </div>
                    <div className="flex items-baseline gap-4">
                        <div className="text-sm text-gray-600 print:text-black">Margine Totale</div>
                        <div className="text-2xl font-bold">{priceFormat(balance?.balance?.totals?.margin)}€</div>
                    </div>
                </div>

                <div className="mt-4">
                    <table className="w-full text-sm print:text-base">
                        <thead>
                        <tr className="border-b border-black">
                            <th className="text-left py-1 font-semibold">&nbsp;</th>
                            <th className="text-right py-1 font-semibold">Costi</th>
                            <th className="text-right py-1 font-semibold">Ricavi</th>
                            <th className="text-right py-1 font-semibold">Profitto</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td className="text-left py-1">AL PIEDE</td>
                            <td className="text-right py-1">{priceFormat(balance?.balance?.per_foot?.cost)}€</td>
                            <td className="text-right py-1">{priceFormat(balance?.balance?.per_foot?.revenue)}€</td>
                            <td className="text-right py-1">{priceFormat(balance?.balance?.per_foot?.margin)}€</td>
                        </tr>
                        <tr>
                            <td className="text-left py-1">ALLA PELLE</td>
                            <td className="text-right py-1">{priceFormat(balance?.balance?.per_leather?.cost)}€</td>
                            <td className="text-right py-1">{priceFormat(balance?.balance?.per_leather?.revenue)}€</td>
                            <td className="text-right py-1">{priceFormat(balance?.balance?.per_leather?.margin)}€</td>
                        </tr>
                        <tr>
                            <td className="text-left py-1">AL METRO</td>
                            <td className="text-right py-1">{priceFormat(balance?.balance?.per_mt?.cost)}€</td>
                            <td className="text-right py-1">{priceFormat(balance?.balance?.per_mt?.revenue)}€</td>
                            <td className="text-right py-1">{priceFormat(balance?.balance?.per_mt?.margin)}€</td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr className="border-t border-black">
                            <td className="text-left py-1 font-semibold">TOTALE</td>
                            <td className="text-right py-1">{priceFormat(balance?.balance?.totals?.cost)}€</td>
                            <td className="text-right py-1">{priceFormat(balance?.balance?.totals?.revenue)}€</td>
                            <td className="text-right py-1 font-semibold">{priceFormat(balance?.balance?.totals?.margin)}€</td>
                        </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div className="bg-white/95 backdrop-blur border-t border-gray-300 shadow-md p-3 flex flex-row justify-between items-center z-20 rounded-t-md print:hidden">
                <div className="flex flex-row items-center gap-2">
                    <PrintButton onClick={handlePrint} />
                    <CSVLink data={buildCsvData()} filename={`${getCsvFileName()}.csv`} separator={";"}>
                        <CsvButton label="CSV Completo" />
                    </CSVLink>
                </div>
                <div className="text-sm pr-2">Riepilogo lotto {nr}</div>
            </div>
        </div>
    );
};

export default LotRecap;
